import { Badge } from '@/components/ui/Badge';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import type { AdminExecution, AutomationEffectObservation } from '@/types/admin.types';
import { AlertTriangle, CheckCircle2, ShieldAlert, XCircle } from 'lucide-react';

interface EffectContractSummaryProps {
  execution: Pick<
    AdminExecution,
    'details' | 'effectAlert' | 'effectIssues' | 'effectOutcome' | 'retryBlocked'
  >;
}

const outcomeLabels = {
  confirmed: 'Effets confirmés',
  failed: 'Effet échoué',
  unconfirmed: 'Effet non prouvé',
  not_applicable: 'Aucun effet à produire',
  legacy_unknown: 'Historique non qualifié',
} as const;

function countLabel(
  effect: AutomationEffectObservation,
  field: 'expected' | 'attempted' | 'confirmed' | 'failed'
) {
  const count = effect[`${field}Count` as keyof AutomationEffectObservation];
  if (typeof count === 'number') return String(count);
  return effect[field] ? 'Oui' : 'Non';
}

function periodLabel(period?: { month: number; year: number } | null) {
  if (!period) return 'Non observée';
  return `${String(period.month).padStart(2, '0')}/${period.year}`;
}

function readableIssue(issue: string) {
  if (issue.includes('send-journal-missing')) return "La preuve d'envoi persistée est incomplète.";
  if (issue.includes('send-journal-failures'))
    return "Le journal d'envoi contient au moins un échec.";
  if (issue.includes('period-mismatch'))
    return 'La période observée ne correspond pas à la période demandée.';
  if (issue.includes('period-unobserved')) return "La période OpenPaye n'a pas pu être confirmée.";
  if (issue.includes('deposit-unconfirmed'))
    return 'Le dépôt EDI a été tenté mais sa preuve persistante manque.';
  if (issue.includes('contract-missing'))
    return "Cette exécution ne contient pas encore de contrat d'effets exploitable.";
  if (issue.includes('configuration-mismatch'))
    return 'La configuration attendue et le résultat enregistré divergent.';
  if (issue.includes('openpaye-result-missing'))
    return 'Le résultat OpenPaye attendu manque dans le journal.';
  if (issue.endsWith(':not-attempted')) return "Un effet attendu n'a pas été tenté.";
  return issue;
}

export function EffectContractSummary({ execution }: EffectContractSummaryProps) {
  const contract = execution.details?.effectContract;
  const reconciliation = execution.details?.effectReconciliation;
  const outcome = contract?.outcome ?? execution.effectOutcome;
  const issues = reconciliation?.issues ?? execution.effectIssues ?? contract?.issues ?? [];

  if (!contract && execution.effectAlert !== true) return null;

  const confirmed = outcome === 'confirmed' || outcome === 'not_applicable';
  const variant = confirmed ? 'success' : outcome === 'legacy_unknown' ? 'warning' : 'danger';

  return (
    <Card
      role={confirmed ? 'status' : 'alert'}
      aria-label="Preuves des effets de l’automatisation"
      className={
        confirmed
          ? 'border-success-200 dark:border-success-500/20'
          : 'border-danger-300 dark:border-danger-500/30'
      }
    >
      <CardHeader
        title="Preuves des effets"
        description="Le statut technique ne suffit pas : chaque effet attendu doit être confirmé."
        icon={
          confirmed ? (
            <CheckCircle2 className="h-5 w-5 text-success-600" />
          ) : (
            <ShieldAlert className="h-5 w-5 text-danger-600" />
          )
        }
        action={outcome ? <Badge variant={variant}>{outcomeLabels[outcome]}</Badge> : undefined}
      />
      <CardContent className="space-y-4">
        {contract?.period && (
          <div className="grid gap-3 rounded-xl bg-neutral-50 p-3 text-sm sm:grid-cols-3 dark:bg-neutral-800/60">
            <div>
              <p className="text-xs text-neutral-500">Période demandée</p>
              <p className="font-medium">{periodLabel(contract.period.requested)}</p>
            </div>
            <div>
              <p className="text-xs text-neutral-500">Période observée</p>
              <p className="font-medium">{periodLabel(contract.period.observed)}</p>
            </div>
            <div>
              <p className="text-xs text-neutral-500">Cohérence</p>
              <Badge variant={contract.period.confirmed ? 'success' : 'danger'}>
                {contract.period.confirmed ? 'Confirmée' : 'Non confirmée'}
              </Badge>
            </div>
          </div>
        )}

        {contract && contract.effects.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] text-left text-sm">
              <thead className="text-xs uppercase text-neutral-500">
                <tr>
                  <th className="px-3 py-2">Effet</th>
                  <th className="px-3 py-2">Attendu</th>
                  <th className="px-3 py-2">Tenté</th>
                  <th className="px-3 py-2">Confirmé</th>
                  <th className="px-3 py-2">Échoué</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                {contract.effects.map((effect) => (
                  <tr key={effect.id}>
                    <td className="px-3 py-3 font-medium text-neutral-900 dark:text-neutral-100">
                      {effect.label}
                    </td>
                    <td className="px-3 py-3">{countLabel(effect, 'expected')}</td>
                    <td className="px-3 py-3">{countLabel(effect, 'attempted')}</td>
                    <td className="px-3 py-3 text-success-700 dark:text-success-400">
                      {countLabel(effect, 'confirmed')}
                    </td>
                    <td className="px-3 py-3 text-danger-700 dark:text-danger-400">
                      {countLabel(effect, 'failed')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {issues.length > 0 && (
          <div className="rounded-xl border border-danger-200 bg-danger-50/70 p-3 dark:border-danger-500/20 dark:bg-danger-500/10">
            <div className="flex items-center gap-2 text-sm font-semibold text-danger-800 dark:text-danger-300">
              <AlertTriangle className="h-4 w-4" />
              Écarts détectés
            </div>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-danger-700 dark:text-danger-300">
              {issues.map((issue) => (
                <li key={issue}>{readableIssue(issue)}</li>
              ))}
            </ul>
          </div>
        )}

        {(execution.retryBlocked ||
          contract?.retryPolicy === 'blocked_sensitive_effect_may_have_occurred') && (
          <div className="flex items-start gap-2 rounded-xl border border-warning-200 bg-warning-50 p-3 text-sm text-warning-800 dark:border-warning-500/20 dark:bg-warning-500/10 dark:text-warning-300">
            <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
            Relance automatique bloquée : un effet sensible a pu avoir lieu. Contrôlez les preuves
            avant toute reprise.
          </div>
        )}

        {reconciliation && (
          <p className="text-xs text-neutral-500">
            Dernière réconciliation : {new Date(reconciliation.checkedAt).toLocaleString('fr-FR')} —
            journal d’envoi : {reconciliation.sendJournal.sent} confirmé(s),{' '}
            {reconciliation.sendJournal.failed} en échec.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
